import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { PricingActions } from 'state/modules/pricing';
import { truncateKeywordSpaces } from 'utils/utils';

const AccountingTypesSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  asyncErrors,
  margin = 'normal',
  resetOnChange = true,
  form: { errors, touched, setFieldValue },
  showModal,
  omitFields,
  omitFieldKey,
  classes,
  required,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState('');
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors && errors[name] && touched[name]) {
      return errors[name];
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (errors && errors[name] && touched && touched[name]) {
      return true;
    }
  };

  const { accountingTypes, fetchingAccountingTypes } = useSelector((state) => state.pricing);

  const fetchAccountingTypes = useCallback(
    debounce(
      (params) => {
        const newParams = { ...params };
        setParams(newParams);
        if (newParams?.keyword) {
          newParams.keyword = truncateKeywordSpaces(newParams.keyword);
        }
        dispatch(PricingActions.fetchAccountingTypes(newParams));
      },
      1000,
      { leading: true, trailing: true }
    ),
    []
  );

  const prevvalue = usePrevious(value);
  const prevParams = usePrevious(customParams);

  useEffect(() => {
    if (!value) return;
    fetchAccountingTypes(params);
  }, []) // eslint-disable-line

  useEffect(() => {
    if (prevParams && customParams && JSON.stringify(prevParams) !== JSON.stringify(customParams)) {
      if (resetOnChange) {
        setFieldValue(name, '');
      }
      fetchAccountingTypes({ ...params, ...customParams });
    }
  }, [customParams]) // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      const defaultVal = accountingTypes.find((el) => el.id === parseInt(value));
      setDefaultValue(defaultVal || '');
    }
  }, [value, accountingTypes]) // eslint-disable-line

  const handleFetchAccountingTypes = () => {
    if (accountingTypes?.length === 0 && !fetchingAccountingTypes && !params?.keyword)
      dispatch(PricingActions.fetchAccountingTypes(params));
  };

  return (
    <>
      <Autocomplete
        label={label}
        margin={margin}
        fullwidth="true"
        variant={variant}
        classes={classes}
        onHighlightChange={handleFetchAccountingTypes}
        error={handleError()}
        loading={fetchingAccountingTypes}
        value={defaultValue}
        id="accountingTypesSelect"
        helperText={handleShowError()}
        getOptionLabel={(option) => option.name || ''}
        options={accountingTypes.filter((value) => omitFields.indexOf(value[omitFieldKey]) === -1)}
        onInputChange={(_, val) => fetchAccountingTypes({ ...params, keyword: val || '' })}
        onChange={(_, val) => {
          setFieldValue(name, val?.id || '');
        }}
        getOptionSelected={(option, value) => option?.id === value?.id}
        required={required}
      />
    </>
  );
};

AccountingTypesSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  asyncErrors: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AccountingTypesSelect.defaultProps = {
  required: false,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  omitFields: [],
  omitFieldKey: '',
  variant: 'standard',
  margin: 'normal',
  label: 'Select an accounting type',
};

export default AccountingTypesSelect;
